import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import React, { useMemo } from 'react'
import { Box, IconButton, Stack, Typography, styled, useMediaQuery, useTheme } from '@mui/material'
import { useRequest } from 'ahooks'
import { BigNumber } from 'ethers'
import { commify, formatUnits } from 'ethers/lib/utils'
import Link from 'next/link'
import MobileDashboardCard from './MobileDashboardCard'
import SwapLinkButton from './SwapLinkButton'
import { ExistingCrossChainAmount } from './ExistingCrossChainAmount'
import Table, { ITableColumn } from '@/components/Table'
import TokenLogo from '@/components/common/TokenLogo'
import { SupportedTicker, SwapInfo, TICKERS, TICKERS_MAP_BY_NAME } from '@/constants/token'
import { CHAIN_INFO, UNISAT_BRC20_URL } from '@/constants'
import { shouldUseValutContract } from '@/utils'
import { getDashboard } from '@/api/network'
import { useUsdPriceOfToken } from '@/hooks/useUsdPriceOfToken'
import useDepositAddressByBtc from '@/hooks/useDepositAddressByBtc'
import { shortenAddress, shortenBtcAddress } from '@/utils/address'
import { RowCenterY } from '@/components/common/Rows'
import { FlexCol } from '@/components/common/Columns'
import { VAULT_ADDRESS } from '@/constants/contract/contractAddress'

const Text1 = styled(Typography)`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`
const Text2 = styled(Typography)`
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
`

interface DashboardData {
  tickerName: SupportedTicker
  logoSrc: any
  supportedChainIds: number[]
  coinGeckoId: string
  swapInfo?: SwapInfo[]
}

const TitleStyled = styled(Typography)`
  font-weight: 400;
  font-size: 36px;
  line-height: 46px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0px 0px 40px #bfff0b;
`

const Dashboard = () => {
  // const [allOverCrossChainVolume, setAllOverCrossChainVolume] = useState(0)

  const data: DashboardData[] = TICKERS.map((t) => ({
    tickerName: t.name,
    logoSrc: t.logoSrcSet.brc,
    supportedChainIds: t.supportChains,
    coinGeckoId: t?.coinGeckoId,
    swapInfo: t?.swapInfo,
  }))

  const { data: depositAddress } = useDepositAddressByBtc()

  const { data: prices } = useUsdPriceOfToken(
    TICKERS.filter((ticker) => !!ticker?.coinGeckoId).map((ticker) => ticker.coinGeckoId),
  )

  const { data: corssChainHistory } = useRequest(async () => {
    const resp = await getDashboard()
    return resp.para
  })

  const getVolumn = (
    targetToken: string,
    input?: {
      coin: string
      amount: string
    }[],
  ) => {
    return input?.reduce((previousValue, currentValue) => {
      if (currentValue.coin === targetToken) {
        return previousValue.add(currentValue.amount)
      } else {
        return previousValue
      }
    }, BigNumber.from(0))
  }

  let allOverCrossChainVolume = 0
  const totalCrossChainVolumeObj: Record<string, number | undefined> = useMemo(() => ({}), [])
  TICKERS.forEach((t) => {
    const volumnOnBtc = getVolumn(t.name, corssChainHistory?.to_btc)
    const volumnOnEvm = getVolumn(t.name, corssChainHistory?.to_evm)

    const price = prices?.[t.coinGeckoId]?.usd

    if (t.name === 'USD0') {
      if (volumnOnBtc && volumnOnEvm) {
        const volumn = Number(formatUnits(volumnOnBtc, 6)) + Number(formatUnits(volumnOnEvm, 18))
        totalCrossChainVolumeObj[t.name] = volumn

        if (price) {
          allOverCrossChainVolume += volumn * price
        }
      } else {
        totalCrossChainVolumeObj[t.name] = undefined
      }
    } else {
      if (volumnOnBtc && volumnOnEvm) {
        const volumn = Number(formatUnits(volumnOnBtc.add(volumnOnEvm)))
        totalCrossChainVolumeObj[t.name] = volumn

        if (price) {
          allOverCrossChainVolume += volumn * price
        }
      } else {
        totalCrossChainVolumeObj[t.name] = undefined
      }
    }
  })

  const columns: ITableColumn<DashboardData>[] = useMemo(
    () => [
      {
        title: 'Ticker',
        render(col, data) {
          return (
            <Stack direction={'row'} spacing={12} alignItems={'center'}>
              <Box width={40} height={40}>
                <TokenLogo
                  src={TICKERS_MAP_BY_NAME[data.tickerName].logoSrcSet.brc}
                  size={40}
                  alt={TICKERS_MAP_BY_NAME[data.tickerName].name}
                />
              </Box>
              <Typography fontSize={20} fontWeight={700} lineHeight={'100%'}>
                {data.tickerName}
              </Typography>
            </Stack>
          )
        },
      },
      {
        title: 'Protogen chain/Target chain',
        render(col, data) {
          if (shouldUseValutContract(data.tickerName)) {
            return (
              <Text1>
                {data.supportedChainIds.map((id) => CHAIN_INFO[id].abbreviation).join(';')}/
                <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Bitcoin
                </Typography>
              </Text1>
            )
          } else
            return (
              <Text1>
                Bitcoin/
                <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {data.supportedChainIds.map((id) => CHAIN_INFO[id].abbreviation).join(';')}
                </Typography>
              </Text1>
            )
        },
      },
      {
        title: 'Total Cross Chain Volume',
        render(col, data) {
          const volumnNumber = totalCrossChainVolumeObj?.[data.tickerName]
          // const volumnNumber = volumn ? Number(formatEther(volumn)) : undefined

          const formatedVolumn = volumnNumber ? commify(volumnNumber.toFixed(2)) : '-'

          const price = prices?.[data?.coinGeckoId]?.usd

          // console.log('value: ', data?.coinGeckoId, volumnNumber, price, price ? volumnNumber * price : 'null')

          return (
            <FlexCol>
              <Text1>{formatedVolumn}</Text1>
              {price && typeof volumnNumber === 'number' ? (
                <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', whiteSpace: 'nowrap' }}>
                  {`$ ${commify((volumnNumber * price).toFixed(2))}`}
                </Typography>
              ) : null}
            </FlexCol>
          )
        },
      },
      {
        title: 'Existing cross-chain amount',
        render(col, data) {
          return <ExistingCrossChainAmount tickerName={data.tickerName} />
        },
      },
      {
        title: 'Attestation Address',
        render(_, data) {
          return (
            <RowCenterY>
              <Text1 sx={{ textDecorationLine: 'underline' }}>
                {shouldUseValutContract(data.tickerName)
                  ? shortenAddress(VAULT_ADDRESS[TICKERS_MAP_BY_NAME[data.tickerName].supportChains[0]])
                  : depositAddress
                  ? shortenBtcAddress(depositAddress)
                  : '-'}
              </Text1>

              <Link
                href={
                  shouldUseValutContract(data.tickerName)
                    ? `${
                        CHAIN_INFO[TICKERS_MAP_BY_NAME[data.tickerName].supportChains[0]].blockExplorers.default.url
                      }/address/${VAULT_ADDRESS[TICKERS_MAP_BY_NAME[data.tickerName].supportChains[0]]}`
                    : UNISAT_BRC20_URL + depositAddress
                }
                target="_blank"
              >
                <IconButton size="small" sx={{ ml: 4 }}>
                  <OpenInNewIcon sx={{ width: 20, height: 20 }} />
                </IconButton>
              </Link>
            </RowCenterY>
          )
        },
      },
      {
        title: 'Price',
        render(col, data) {
          const price = prices?.[data?.coinGeckoId]?.usd

          return <Text1 sx={{ whiteSpace: 'nowrap' }}>{price ? `$ ${commify(Number(price).toFixed(8))}` : '-'}</Text1>
        },
      },
      {
        title: null,
        render(col, data) {
          if (data?.swapInfo) {
            return (
              <RowCenterY sx={{ columnGap: 4 }}>
                {data.swapInfo.map((swap) => (
                  <SwapLinkButton key={swap.url} swap={swap.name} href={swap.url} />
                ))}
              </RowCenterY>
            )
          } else return null
        },
      },
    ],
    [depositAddress, prices, totalCrossChainVolumeObj],
  )

  const theme = useTheme()
  const isMD = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <Stack direction={'column'} spacing={24} mt={isMD ? 68 : 20} mb={isMD ? 82 : 30} px={!isMD && 24}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, rowGap: 20 }}>
        <TitleStyled variant="h1">Dashboard</TitleStyled>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'unset', md: 'center' },
            rowGap: 8,
            ml: { xs: 'unset', md: 'auto' },
          }}
        >
          <Text1 sx={{ opacity: 0.7 }}>Total Cross Chain Volume</Text1>

          <Text2 sx={{ ml: { xs: 0, md: 12 } }}>$ {prices ? commify(allOverCrossChainVolume.toFixed(2)) : '-'}</Text2>
        </Box>
      </Box>

      {isMD ? (
        <Table datas={data} columns={columns} isDashboard={true}></Table>
      ) : (
        <Stack spacing={20}>
          {data.map((d) => (
            <MobileDashboardCard
              key={d.tickerName}
              logoSrc={d.logoSrc}
              tickerName={d.tickerName}
              price={prices?.[d?.coinGeckoId]?.usd}
              totalCrossChainVolume={totalCrossChainVolumeObj?.[d.tickerName]}
              allOverCrossChainVolume={prices ? commify(allOverCrossChainVolume.toFixed(2)) : '-'}
              swapInfo={d?.swapInfo}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}

export default Dashboard
